import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { smoothScrollTo } from "@/hooks/useSmoothScroll";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";

export default function Navbar() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === "/";

  // Section links scroll on the landing page; navigate-then-scroll otherwise.
  const sectionLinks = [
    { id: "products", label: t("nav.products") },
    { id: "technology", label: t("nav.technology") },
    { id: "applications", label: t("nav.applications") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setMobileOpen(false);
    if (!isLanding) {
      // Navigate to the landing page and scroll after the route swap.
      navigate(`/#${id}`);
      // After navigation, scroll once the section exists in DOM.
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) smoothScrollTo(el, { offset: -64 });
      }, 60);
      return;
    }
    const el = document.getElementById(id);
    if (el) smoothScrollTo(el, { offset: -64 });
  };

  const goHome = () => {
    setMobileOpen(false);
    if (!isLanding) {
      navigate("/");
      return;
    }
    smoothScrollTo(0);
  };

  const goFaq = () => setMobileOpen(false);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/75 backdrop-blur-2xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <button
          data-testid="nav-logo"
          onClick={goHome}
          className="font-display text-lg md:text-xl font-medium tracking-tight text-[#1D1D1F]"
        >
          Privacy<span className="font-light text-[#86868B]">Glass</span>
          <sup className="ml-0.5 text-[0.45em] font-light text-[#86868B] align-super">™</sup>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {sectionLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goSection(l.id)}
              className="text-sm font-body font-medium text-[#1D1D1F]/80 hover:text-[#1D1D1F] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/faq"
            data-testid="nav-link-faq"
            onClick={goFaq}
            className="text-sm font-body font-medium text-[#1D1D1F]/80 hover:text-[#1D1D1F] transition-colors"
          >
            {t("nav.faq")}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            data-testid="nav-inquire-btn"
            onClick={() => goSection("contact")}
            className="hidden sm:inline-flex items-center justify-center bg-[#1D1D1F] hover:bg-black text-white text-sm font-body font-medium rounded-full px-5 py-2.5 transition-colors"
          >
            {t("nav.inquire")}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="menu"
            className="md:hidden h-10 w-10 grid place-items-center rounded-full hover:bg-black/5"
          >
            <span className="block w-4 h-px bg-[#1D1D1F]" />
            <span className="block w-4 h-px bg-[#1D1D1F] mt-1.5" />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-black/5 bg-white/95 backdrop-blur-xl"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {sectionLinks.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => goSection(l.id)}
                className="text-left py-3 text-base font-body text-[#1D1D1F]"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/faq"
              data-testid="mobile-nav-faq"
              onClick={goFaq}
              className="text-left py-3 text-base font-body text-[#1D1D1F]"
            >
              {t("nav.faq")}
            </Link>
            <button
              data-testid="mobile-nav-contact"
              onClick={() => goSection("contact")}
              className="mt-2 bg-[#1D1D1F] text-white text-sm font-medium rounded-full px-5 py-3"
            >
              {t("nav.inquire")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
